import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getApiUrl } from "../api";
import { getStoredToken } from "../auth";
import FamilyFileGate from "./FamilyFileGate";
import { getCustomerDocumentOutcome, getDocumentLoadingCopy } from "../utils/documentValidationChecks";

const employmentStatusOptions = [
  "Employed",
  "Self-Employed",
  "Business Owner",
  "Student",
  "Retired",
  "Unemployed",
  "Homemaker",
  "Freelancer",
  "Child",
  "Other",
];

const employedStatuses = new Set([
  "Employed",
  "Self-Employed",
  "Business Owner",
  "Freelancer",
]);

const requiresIncomeEvidence = (member) => employedStatuses.has(member.employmentStatus) ||
  Number(member.monthlyincome || 0) > 0 ||
  Number(member.annualincome || 0) > 0 ||
  Number(member.otherincome || 0) > 0;

const isVerifiedStatus = (status) => ["verified", "accepted"].includes(String(status || "").toLowerCase());

const normalizedFamilyStatus = (status) => String(status || "").trim().toUpperCase().replace(/[\s-]+/g, "_");

const supportingDocumentTypes = {
  EMIRATES_ID: { titleEn: "Emirates ID", titleAr: "الهوية الإماراتية" },
  PASSPORT: { titleEn: "Passport", titleAr: "جواز السفر" },
  RESIDENCY: { titleEn: "Residency Document", titleAr: "مستند الإقامة" },
  MARRIAGE_CERTIFICATE: { titleEn: "Marriage Certificate", titleAr: "شهادة الزواج" },
  EMPLOYMENT_EVIDENCE: { titleEn: "Employment Evidence", titleAr: "إثبات العمل" },
  OTHER: { titleEn: "Other Supporting Evidence", titleAr: "إثبات داعم آخر" },
};

const emptyMember = () => ({
  memberType: "OTHER",
  relation: "",
  name: "",
  age: "",
  employmentStatus: "",
  employer: "",
  occupation: "",
  monthlyincome: "",
  annualincome: "",
  otherincome: "",
  salaryCertificate: "",
  salaryCertificateValidationToken: "",
  salaryCertificateFileName: "",
  incomeVerificationStatus: "Pending",
  eidnumber: "",
  expirydate: "",
  dateOfBirth: "",
  gender: "",
  nationality: "",
  passportNumber: "",
  passportExpiry: "",
  residencyStatus: "",
  residencyExpiry: "",
  sponsorshipStatus: "",
  sponsorshipDetails: "",
  marriageDetails: "",
  verificationStatus: "Review Required",
  linkedAutomatically: false,
});

export default function BeneficiaryFamilyDetailsForm({ language = "en", onSaved }) {
  const isArabic = language === "ar";
  const t = (english, arabic) => isArabic ? arabic : english;
  const formRef = useRef(null);
  const [beneficiaryId, setBeneficiaryId] = useState("");
  const [numberOfDependents, setNumberOfDependents] = useState(0);
  const [members, setMembers] = useState([]);
  const [relations, setRelations] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [residencyStatuses, setResidencyStatuses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [invalidMemberFields, setInvalidMemberFields] = useState({});
  const [aiDocumentModal, setAiDocumentModal] = useState(null);
  const [familyFileStatus, setFamilyFileStatus] = useState(null);
  const [headProfile, setHeadProfile] = useState({});
  const [familySummary, setFamilySummary] = useState({});
  const [familyDocuments, setFamilyDocuments] = useState([]);
  const [documentDrafts, setDocumentDrafts] = useState({});
  const [previewDocument, setPreviewDocument] = useState(null);
  const token = getStoredToken();

  useEffect(() => {
    const objectUrl = previewDocument?.objectUrl;
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [previewDocument?.objectUrl]);

  const openDocumentPreview = async (documentRecord, memberName) => {
    const requestKey = `${documentRecord.id}-${Date.now()}`;
    setPreviewDocument({ ...documentRecord, memberName, requestKey, loading: true, objectUrl: "", error: "" });
    try {
      const response = await fetch(getApiUrl(documentRecord.url), {
        credentials: "include",
        headers: { Accept: "application/pdf,image/*,*/*", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.message || t("Unable to load this document.", "تعذر تحميل هذا المستند."));
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      setPreviewDocument((current) => {
        if (current?.requestKey === requestKey) return { ...current, loading: false, objectUrl };
        URL.revokeObjectURL(objectUrl);
        return current;
      });
    } catch (error) {
      setPreviewDocument((current) => current?.requestKey === requestKey
        ? { ...current, loading: false, error: error.message || t("Unable to load this document.", "تعذر تحميل هذا المستند.") }
        : current);
    }
  };

  const scrollToFormTop = () => {
    window.requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const applyResult = (result) => {
    const count = Number(result.numberOfDependents || 0);
    const loadedMembers = Array.isArray(result.members) ? result.members : [];
    const loadedNationalities = Array.isArray(result.nationalities) ? result.nationalities : [];
    const loadedResidencyStatuses = Array.isArray(result.residencyStatuses) ? result.residencyStatuses : [];
    const normalizedMembers = loadedMembers.map((member) => {
      const storedNationality = String(member.nationality || "").trim();
      const nationality = loadedNationalities.find((option) => [option.id, option.title_en, option.title_ar]
        .some((value) => String(value || "").trim().toLowerCase() === storedNationality.toLowerCase()));
      const storedResidencyStatus = String(member.residencyStatus || "").trim();
      const residencyStatus = loadedResidencyStatuses.find((option) => [option.id, option.title_en, option.title_ar]
        .some((value) => String(value || "").trim().toLowerCase() === storedResidencyStatus.toLowerCase()));
      return {
        ...member,
        nationality: nationality ? String(nationality.id) : storedNationality,
        residencyStatus: residencyStatus ? String(residencyStatus.id) : storedResidencyStatus,
      };
    });
    setBeneficiaryId(result.beneficiaryId || "");
    setRelations(Array.isArray(result.relations) ? result.relations : []);
    setNationalities(loadedNationalities);
    setResidencyStatuses(loadedResidencyStatuses);
    setHeadProfile(result.head || {});
    setFamilySummary(result.summary || {});
    setFamilyDocuments(Array.isArray(result.documents) ? result.documents : []);
    setNumberOfDependents(count);
    setMembers(Array.from({ length: count }, (_, index) => ({
      ...emptyMember(),
      ...(normalizedMembers[index] || {}),
      relation: normalizedMembers[index]?.relation ? String(normalizedMembers[index].relation) : "",
    })));
  };

  const registeredGender = String(headProfile.gender || "").trim().toLowerCase();
  const registrantIsFemale = ["female", "f", "أنثى", "انثى"].includes(registeredGender);
  const registrantIsMale = ["male", "m", "ذكر"].includes(registeredGender);
  const spouseLabel = registrantIsFemale
    ? t("Husband", "الزوج")
    : registrantIsMale
      ? t("Wife", "الزوجة")
      : t("Spouse", "الزوج / الزوجة");
  const spouseAddedMessage = isArabic ? `تمت إضافة ملف ${spouseLabel}` : `${spouseLabel} profile added`;
  const spouseAddPrompt = isArabic ? `اختر نعم لإضافة ${spouseLabel}` : `Select Yes to add ${spouseLabel}`;
  const spouseGender = registrantIsFemale ? "Male" : registrantIsMale ? "Female" : "";
  const spouseRelationName = registrantIsFemale ? "husband" : registrantIsMale ? "wife" : "";
  const spouseRelationId = String(relations.find((relation) =>
    String(relation.title_en || relation.title || "").trim().toLowerCase() === spouseRelationName
  )?.id || "");

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      try {
        const headers = { Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
        const [response, familyFileResponse] = await Promise.all([
          fetch(getApiUrl("/api/BeneficiaryFamilyDetails.ashx"), { credentials: "include", headers, signal: controller.signal }),
          fetch(getApiUrl("/api/UnifiedFamilyFile.ashx"), { credentials: "include", headers, signal: controller.signal }),
        ]);
        const [result, familyFileResult] = await Promise.all([
          response.json().catch(() => ({})),
          familyFileResponse.json().catch(() => ({})),
        ]);
        if (!response.ok || !result.success) throw new Error(result.message || "Unable to load family details.");
        if (!familyFileResponse.ok || !familyFileResult.success) throw new Error(familyFileResult.message || "Unable to load the Unified Family File.");
        applyResult(result);
        setFamilyFileStatus(familyFileResult);
      } catch (error) {
        if (error.name !== "AbortError") {
          setIsError(true);
          setMessage(error.message || "Unable to load family details.");
        }
      } finally {
        setIsLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [token]);

  useEffect(() => {
    if (!spouseGender || !spouseRelationId) return;
    setMembers((current) => {
      let changed = false;
      const next = current.map((member) => {
        if (member.memberType !== "SPOUSE"
          || (member.gender === spouseGender && String(member.relation) === spouseRelationId)) return member;
        changed = true;
        return { ...member, gender: spouseGender, relation: spouseRelationId };
      });
      return changed ? next : current;
    });
  }, [spouseGender, spouseRelationId]);

  const addMember = (memberType) => {
    setMembers((current) => [...current, {
      ...emptyMember(),
      memberType,
      ...(memberType === "SPOUSE" ? { gender: spouseGender, relation: spouseRelationId } : {}),
    }]);
    setNumberOfDependents((current) => current + 1);
    setMessage("");
  };

  const updateDocumentDraft = (index, field) => (event) => {
    const value = event.target.value;
    setDocumentDrafts((current) => ({
      ...current,
      [index]: { category: "EMIRATES_ID", expiryDate: "", ...(current[index] || {}), [field]: value },
    }));
  };

  const saveMemberDocuments = async (index, files, member, draftOverride) => {
    if (!files.length || !member?.id) return;
    const draft = { category: "EMIRATES_ID", expiryDate: "", ...(draftOverride || documentDrafts[index] || {}) };
    const documentType = supportingDocumentTypes[draft.category] || supportingDocumentTypes.OTHER;
    setDocumentDrafts((current) => ({ ...current, [index]: { ...draft, busy: true } }));
    try {
      const uploaded = [];
      for (const file of files) {
        setAiDocumentModal({ state: "loading", title: t(documentType.titleEn, documentType.titleAr) });
        const validationData = new FormData();
        validationData.append("document", file, file.name);
        validationData.append("documentType", documentType.titleEn);
        validationData.append("documentTitleAr", documentType.titleAr);
        validationData.append("verificationPurpose", "familySupportingDocument");
        validationData.append("familyDocumentCategory", draft.category);
        validationData.append("expectedName", member.name || "");
        validationData.append("expectedEmiratesId", member.eidnumber || "");
        validationData.append("language", isArabic ? "ar" : "en");
        const validationResponse = await fetch(getApiUrl("/api/AidDocumentValidation.ashx"), {
          method: "POST",
          credentials: "include",
          headers: { Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: validationData,
        });
        const validationResult = await validationResponse.json().catch(() => ({}));
        if (!validationResponse.ok || !validationResult.success) throw new Error(validationResult.message || t("Unable to verify this document.", "تعذر التحقق من هذا المستند."));
        const state = ["accepted", "review", "rejected"].includes(String(validationResult.status).toLowerCase())
          ? String(validationResult.status).toLowerCase() : "review";
        if (state === "rejected") {
          setAiDocumentModal({ state, title: t(documentType.titleEn, documentType.titleAr), outcomeCode: validationResult.outcomeCode, customerMessage: validationResult.customerMessage });
          break;
        }
        if (!validationResult.docurl || !validationResult.validationToken) throw new Error(t("Rahma verified the document but Cloudflare storage was not completed.", "تحققت رحمة من المستند، ولكن لم يكتمل الحفظ على Cloudflare."));
        const saveData = new FormData();
        saveData.append("familyMemberId", String(member.id));
        saveData.append("category", draft.category);
        saveData.append("title", file.name);
        saveData.append("expiryDate", draft.expiryDate || "");
        saveData.append("validationToken", validationResult.validationToken);
        const saveResponse = await fetch(getApiUrl("/api/FamilyMemberDocument.ashx"), {
          method: "POST",
          credentials: "include",
          headers: { Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: saveData,
        });
        const saveResult = await saveResponse.json().catch(() => ({}));
        if (!saveResponse.ok || !saveResult.success) throw new Error(saveResult.message || t("Unable to save the verified document.", "تعذر حفظ المستند الذي تم التحقق منه."));
        uploaded.push(saveResult.document);
        setAiDocumentModal({ state, title: t(documentType.titleEn, documentType.titleAr), outcomeCode: validationResult.outcomeCode, customerMessage: validationResult.customerMessage });
      }
      if (uploaded.length) {
        setFamilyDocuments((current) => [...uploaded.reverse(), ...current]);
        setIsError(false);
        setMessage(t("Rahma checked the documents and saved them securely on Cloudflare.", "تحققت رحمة من المستندات وحفظتها بأمان على Cloudflare."));
      }
    } catch (error) {
      setIsError(true);
      const customerMessage = error.message || t("Unable to verify or save this document.", "تعذر التحقق من هذا المستند أو حفظه.");
      setMessage(customerMessage);
      setAiDocumentModal({ state: "review", title: t(documentType.titleEn, documentType.titleAr), customerMessage });
    } finally {
      setDocumentDrafts((current) => ({
        ...current,
        [index]: { ...draft, pendingFiles: [], pendingFileNames: [], busy: false },
      }));
    }
  };

  const uploadMemberDocuments = (index) => async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    const member = members[index];
    if (!member?.id) {
      const fileNames = files.map((file) => file.name);
      setDocumentDrafts((current) => ({
        ...current,
        [index]: {
          category: "EMIRATES_ID",
          expiryDate: "",
          ...(current[index] || {}),
          pendingFiles: files,
          pendingFileNames: fileNames,
        },
      }));
      setIsError(false);
      setMessage(t(
        "Document selected. Save Family Details to upload it.",
        "تم اختيار المستند. احفظ بيانات الأسرة لرفعه."
      ));
      return;
    }
    await saveMemberDocuments(index, files, member);
  };

  const updateMember = (index, field) => (event) => {
    const value = event.target.value;
    setInvalidMemberFields((current) => ({
      ...current,
      [index]: (current[index] || []).filter((invalidField) => invalidField !== field),
    }));
    setMembers((current) => current.map((member, memberIndex) =>
      memberIndex === index ? {
        ...member,
        [field]: value,
        ...(["name", "employmentStatus", "employer", "occupation", "monthlyincome", "annualincome", "otherincome"].includes(field)
          ? { incomeVerificationStatus: "Pending", salaryCertificateValidationToken: "" }
          : {}),
      } : member
    ));
  };

  const updateEmploymentStatus = (index) => (event) => {
    const employmentStatus = event.target.value;
    setInvalidMemberFields((current) => ({
      ...current,
      [index]: (current[index] || []).filter((field) => !["employmentStatus", "employer", "occupation", "monthlyincome", "annualincome", "otherincome", "salaryCertificate", "incomeVerificationStatus"].includes(field)),
    }));
    setMembers((current) => current.map((member, memberIndex) => {
      if (memberIndex !== index) return member;

      const isEmployed = employedStatuses.has(employmentStatus);
      const wasIncomeRequired = requiresIncomeEvidence(member);
      return {
        ...member,
        employmentStatus,
        employer: isEmployed ? member.employer : "",
        occupation: isEmployed ? member.occupation : "",
        monthlyincome: isEmployed ? (wasIncomeRequired ? member.monthlyincome : "") : "0",
        annualincome: isEmployed ? (wasIncomeRequired ? member.annualincome : "") : "0",
        otherincome: isEmployed ? (wasIncomeRequired ? member.otherincome : "") : "0",
        incomeVerificationStatus: isEmployed ? "Pending" : "Not Required",
        salaryCertificateValidationToken: "",
      };
    }));
  };

  const updateCertificate = (index) => async (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = "";
    if (!file) return;

    const member = members[index];
    const title = t("Employment / Salary Certificate", "شهادة العمل / الراتب");
    if (!member.name.trim()) {
      setAiDocumentModal({
        state: "review",
        title,
        score: 0,
        checks: [],
        notice: t("Enter the family member name before verifying the certificate.", "أدخل اسم فرد الأسرة قبل التحقق من الشهادة."),
      });
      return;
    }
    setMembers((current) => current.map((item, memberIndex) =>
      memberIndex === index ? { ...item, incomeVerificationStatus: "Verifying", salaryCertificateValidationToken: "" } : item
    ));
    setAiDocumentModal({ state: "loading", title });

    try {
      const formData = new FormData();
      formData.append("document", file, file.name);
      formData.append("documentType", "Employment / Salary Certificate");
      formData.append("documentTitleAr", "شهادة العمل / الراتب");
      formData.append("verificationPurpose", "familySalaryCertificate");
      formData.append("expectedName", member.name || "");
      formData.append("language", isArabic ? "ar" : "en");
      const response = await fetch(getApiUrl("/api/AidDocumentValidation.ashx"), {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: formData,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) throw new Error(result.message || t("Unable to verify this certificate.", "تعذر التحقق من هذه الشهادة."));
      const state = ["accepted", "review", "rejected"].includes(String(result.status).toLowerCase())
        ? String(result.status).toLowerCase() : "review";
      const accepted = state === "accepted" && result.docurl && result.validationToken;
      if (accepted) {
        setInvalidMemberFields((current) => ({
          ...current,
          [index]: (current[index] || []).filter((field) => !["salaryCertificate", "incomeVerificationStatus"].includes(field)),
        }));
      }
      setMembers((current) => current.map((item, memberIndex) => memberIndex === index ? {
        ...item,
        ...(accepted ? {
          salaryCertificate: result.docurl,
          salaryCertificateValidationToken: result.validationToken,
          salaryCertificateFileName: file.name,
          incomeVerificationStatus: "Verified",
        } : { incomeVerificationStatus: isVerifiedStatus(member.incomeVerificationStatus) ? member.incomeVerificationStatus : "Pending" }),
      } : item));
      setAiDocumentModal({
        state,
        title,
        outcomeCode: result.outcomeCode,
        customerMessage: result.customerMessage,
      });
    } catch (error) {
      setMembers((current) => current.map((item, memberIndex) => memberIndex === index
        ? { ...item, incomeVerificationStatus: isVerifiedStatus(member.incomeVerificationStatus) ? member.incomeVerificationStatus : "Pending" }
        : item));
      setAiDocumentModal({ state: "review", title, score: 0, checks: [], notice: error.message || t("Unable to verify this certificate.", "تعذر التحقق من هذه الشهادة.") });
    }
  };

  const deleteMember = (index) => {
    const member = members[index];
    if (member?.id && !window.confirm(`Delete dependent ${index + 1}? This will be applied when you save.`)) {
      return;
    }

    const nextCount = Math.max(0, numberOfDependents - 1);
    setMembers((current) => current.filter((_, memberIndex) => memberIndex !== index));
    setNumberOfDependents(nextCount);
    setIsError(false);
    setMessage("Dependent removed. Click Save Family Details to update the database.");
  };

  const save = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setIsError(false);
    setMessage("");
    try {
      const nextInvalidFields = {};
      members.forEach((member, index) => {
        const fields = [];
        if (!String(member.relation || "").trim()) fields.push("relation");
        if (!String(member.name || "").trim()) fields.push("name");
        if (!String(member.memberType || "").trim()) fields.push("memberType");
        if (!String(member.dateOfBirth || "").trim()) fields.push("dateOfBirth");
        if (!String(member.nationality || "").trim()) fields.push("nationality");
        if (member.memberType === "CHILD" && !String(member.gender || "").trim()) fields.push("gender");
        if (String(member.age ?? "").trim() === "") fields.push("age");
        if (!String(member.employmentStatus || "").trim()) fields.push("employmentStatus");
        if (!String(member.eidnumber || "").trim()) fields.push("eidnumber");
        if (!member.expirydate) fields.push("expirydate");
        if (requiresIncomeEvidence(member)) {
          if (!String(member.employer || "").trim()) fields.push("employer");
          if (!String(member.occupation || "").trim()) fields.push("occupation");
          if (String(member.monthlyincome ?? "").trim() === "") fields.push("monthlyincome");
          if (String(member.annualincome ?? "").trim() === "") fields.push("annualincome");
          if (String(member.otherincome ?? "").trim() === "") fields.push("otherincome");
          if (!member.salaryCertificate) fields.push("salaryCertificate");
          if (!isVerifiedStatus(member.incomeVerificationStatus)) fields.push("incomeVerificationStatus");
        }
        if (fields.length) nextInvalidFields[index] = fields;
      });
      setInvalidMemberFields(nextInvalidFields);
      const incompleteIndex = members.findIndex((_, index) => nextInvalidFields[index]?.length);
      if (incompleteIndex >= 0) {
        throw new Error(t(
          `Please complete all mandatory fields for dependent ${incompleteIndex + 1}. Employment and income fields require an AI-verified certificate.`,
          `يرجى استكمال جميع الحقول الإلزامية للمعال ${incompleteIndex + 1}. تتطلب بيانات العمل والدخل شهادة تم التحقق منها بالذكاء الاصطناعي.`
        ));
      }
      const unverifiedIndex = members.findIndex((member) => requiresIncomeEvidence(member) && !isVerifiedStatus(member.incomeVerificationStatus));
      if (unverifiedIndex >= 0) {
        throw new Error(t(
          `Verify the salary certificate for dependent ${unverifiedIndex + 1} before saving.`,
          `يرجى التحقق من شهادة الراتب للمعال ${unverifiedIndex + 1} قبل الحفظ.`
        ));
      }
      const formData = new FormData();
      formData.append("numberOfDependents", String(numberOfDependents));
      formData.append("members", JSON.stringify(members.map(({
        id, relation, name, age, employmentStatus, employer, occupation,
        monthlyincome, annualincome, otherincome, salaryCertificate,
        salaryCertificateValidationToken, incomeVerificationStatus, eidnumber, expirydate,
        memberType,dateOfBirth,gender,nationality,passportNumber,passportExpiry,residencyStatus,
        residencyExpiry,sponsorshipStatus,sponsorshipDetails,marriageDetails,verificationStatus,linkedAutomatically,
      }) => ({
        id: Number(id || 0),
        relation: Number(relation),
        name,
        age,
        employmentStatus,
        employer,
        occupation,
        monthlyincome,
        annualincome,
        otherincome,
        salaryCertificate,
        salaryCertificateValidationToken,
        incomeVerificationStatus,
        eidnumber,
        expirydate,
        memberType,dateOfBirth,gender,nationality,passportNumber,passportExpiry,residencyStatus,
        residencyExpiry,sponsorshipStatus,sponsorshipDetails,marriageDetails,verificationStatus,linkedAutomatically,
      }))));
      const response = await fetch(getApiUrl("/api/BeneficiaryFamilyDetails.ashx"), {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) throw new Error(result.message || "Unable to save family details.");
      const pendingDocumentUploads = members.map((_, index) => ({
        index,
        files: documentDrafts[index]?.pendingFiles || [],
        draft: documentDrafts[index],
        member: result.members?.[index],
      })).filter(({ files, member }) => files.length && member?.id);
      applyResult(result);
      onSaved?.({ members: result.members || [], relations: result.relations || relations, numberOfDependents: result.numberOfDependents });
      setInvalidMemberFields({});
      setMessage(result.message || "Family details saved successfully.");
      for (const pendingUpload of pendingDocumentUploads) {
        await saveMemberDocuments(pendingUpload.index, pendingUpload.files, pendingUpload.member, pendingUpload.draft);
      }
      scrollToFormTop();
    } catch (error) {
      setIsError(true);
      setMessage(error.message || "Unable to save family details.");
      scrollToFormTop();
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <div className="family-details-loading">{t("Loading family details...", "جارٍ تحميل بيانات الأسرة...")}</div>;

  const correctionRequired = familyFileStatus?.correctionRequired === true ||
    familyFileStatus?.verificationStage === "RETURNED_FOR_CORRECTION";
  const canManageFamily = correctionRequired || (
    Number(familyFileStatus?.familyFile?.id || 0) > 0 &&
    familyFileStatus?.familyFile?.membershipRole === "HEAD" &&
    familyFileStatus?.canClaimHead !== false
  );
  if (!canManageFamily) return familyFileStatus ? <FamilyFileGate status={familyFileStatus} token={token} language={language} onStatusChange={setFamilyFileStatus} /> : null;

  const familyStatusCode = normalizedFamilyStatus(familyFileStatus?.familyFile?.status || familySummary.familyVerificationStatus);
  const familyIsVerified = familyFileStatus?.eligible === true || familyStatusCode === "VERIFIED";
  const familyStatusLabel = familyIsVerified
    ? t("Verified", "تم التحقق")
    : familyStatusCode === "RETURNED_FOR_CORRECTION"
      ? t("Correction required", "مطلوب تصحيح")
      : t("Review required", "مطلوب مراجعة");
  const headInitial = String(headProfile.fullName || "H").trim().charAt(0).toUpperCase();

  const modalDetails = getCustomerDocumentOutcome(aiDocumentModal, isArabic) || ({
    accepted: {
      title: t("Certificate Accepted", "تم قبول الشهادة"),
      message: t("AI verification completed successfully.", "اكتمل التحقق بالذكاء الاصطناعي بنجاح."),
      notice: t("The certificate is verified. You may now save and proceed.", "تم التحقق من الشهادة. يمكنك الآن الحفظ والمتابعة."),
      button: t("Great! Continue", "متابعة"),
    },
    review: {
      title: t("Verification Not Completed", "لم يكتمل التحقق"),
      message: t("This certificate cannot be used until AI verification accepts it.", "لا يمكن استخدام هذه الشهادة حتى يقبلها التحقق بالذكاء الاصطناعي."),
      notice: t("Please upload another clear and complete certificate.", "يرجى رفع شهادة أخرى واضحة وكاملة."),
      button: t("Close", "إغلاق"),
    },
    rejected: {
      title: t("Certificate Rejected", "تم رفض الشهادة"),
      message: t("AI verification found problems with this certificate.", "وجد التحقق بالذكاء الاصطناعي مشكلات في هذه الشهادة."),
      notice: t("Please upload a valid certificate matching the family member.", "يرجى رفع شهادة صالحة ومطابقة لفرد الأسرة."),
      button: t("Close", "إغلاق"),
    },
  }[aiDocumentModal?.state]);
  const documentLoadingCopy = getDocumentLoadingCopy(aiDocumentModal?.title, isArabic);

  return (
    <>
    <FamilyFileGate status={familyFileStatus} token={token} language={language} onStatusChange={setFamilyFileStatus} />
    <form ref={formRef} className="beneficiary-family-form" onSubmit={save} noValidate>
      {message ? (
        <div className={`beneficiary-family-message ${isError ? "error" : "success"}`} role="alert">
          {message}
        </div>
      ) : null}

      <section className={`unified-family-head ${familyIsVerified ? "is-verified" : "is-review"}`} aria-label={t("Head of Household profile", "ملف رب الأسرة")}>
        <div className="unified-family-head-identity">
          <span className="unified-family-head-avatar" aria-hidden="true">{headInitial}</span>
          <div>
            <span className="unified-family-head-eyebrow"><i className="fa-solid fa-people-roof"></i>{t("Head of Household", "رب الأسرة")}</span>
            <h3>{headProfile.fullName || "—"}</h3>
            <div className="unified-family-head-badges">
              <span className={`unified-family-status-badge ${familyIsVerified ? "is-verified" : "is-review"}`}><i className={`fa-solid ${familyIsVerified ? "fa-circle-check" : "fa-clock"}`}></i>{familyStatusLabel}</span>
              <span className={`unified-family-uae-badge ${headProfile.uaePassVerified ? "is-verified" : ""}`}><i className="fa-solid fa-shield-halved"></i>{headProfile.uaePassVerified ? t("UAE PASS verified", "موثق عبر UAE PASS") : t("UAE PASS verification required", "مطلوب التحقق عبر UAE PASS")}</span>
            </div>
          </div>
        </div>
        <dl className="unified-family-head-details">
          <div><dt>{t("Emirates ID", "الهوية الإماراتية")}</dt><dd>{headProfile.emiratesId || "—"}</dd></div>
          <div><dt>{t("Employment", "حالة العمل")}</dt><dd>{headProfile.employmentStatus || "—"}</dd></div>
          <div><dt>{t("Employer", "جهة العمل")}</dt><dd>{headProfile.employerName || t("Not provided", "غير محدد")}</dd></div>
          <div><dt>{t("Verified monthly income", "الدخل الشهري الموثق")}</dt><dd>{headProfile.monthlySalary || "0"} <small>{t("AED", "درهم")}</small></dd></div>
        </dl>
      </section>
      <section className="unified-family-summary" aria-label={t("Family profile summary", "ملخص ملف الأسرة")}>
        {[
          [t("Family members", "أفراد الأسرة"), familySummary.totalFamilyMembers || 1],
          [t("Children", "الأطفال"), familySummary.numberOfChildren || 0],
          [t("Employed", "العاملون"), familySummary.employedMembers || 0],
          [t("Unemployed", "غير العاملين"), familySummary.unemployedMembers || 0],
          [t("Verified monthly income", "الدخل الشهري الموثق"), familySummary.totalVerifiedMonthlyIncome || 0],
          [t("Sponsored members", "الأفراد المكفولون"), familySummary.totalSponsoredMembers || 0],
          [t("Missing documents", "المستندات الناقصة"), familySummary.missingDocuments || 0],
          [t("Family status", "حالة الأسرة"), familyStatusLabel],
        ].map(([label, value]) => <article key={label}><span>{label}</span><strong>{value}</strong></article>)}
      </section>
      <div className="family-profile-actions">
        <div className="family-profile-action-card"><span>{t("Are you married?", "هل أنت متزوج؟")}</span><select aria-label={t("Are you married?", "هل أنت متزوج؟")} value={members.some((member) => member.memberType === "SPOUSE") ? "yes" : "no"} onChange={(event) => event.target.value === "yes" && !members.some((member) => member.memberType === "SPOUSE") && addMember("SPOUSE")}><option value="no">{t("No", "لا")}</option><option value="yes">{t("Yes", "نعم")}</option></select><small>{members.some((member) => member.memberType === "SPOUSE") ? spouseAddedMessage : spouseAddPrompt}</small></div>
        <div className="family-profile-action-card"><span>{t(`How many children do you have? ${members.filter((member) => member.memberType === "CHILD").length}`, `كم عدد أطفالك؟ ${members.filter((member) => member.memberType === "CHILD").length}`)}</span><button type="button" onClick={() => addMember("CHILD")}><i className="fa-solid fa-child-reaching"></i>{t("Add Child", "إضافة طفل")}</button><small>{t("Add each child to the Family File", "أضف كل طفل إلى ملف الأسرة")}</small></div>
        <div className="family-profile-action-card"><span>{t("Do you sponsor anyone else?", "هل تكفل شخصاً آخر؟")}</span><select aria-label={t("Do you sponsor anyone else?", "هل تكفل شخصاً آخر؟")} value={members.some((member) => member.memberType === "SPONSORED") ? "yes" : "no"} onChange={(event) => event.target.value === "yes" && !members.some((member) => member.memberType === "SPONSORED") && addMember("SPONSORED")}><option value="no">{t("No", "لا")}</option><option value="yes">{t("Yes", "نعم")}</option></select><small>{members.some((member) => member.memberType === "SPONSORED") ? t("Sponsored member profile added", "تمت إضافة ملف الفرد المكفول") : t("Select Yes to add a sponsored member", "اختر نعم لإضافة فرد مكفول")}</small>{members.some((member) => member.memberType === "SPONSORED") ? <button className="family-profile-action-secondary" type="button" onClick={() => addMember("SPONSORED")}><i className="fa-solid fa-people-group"></i>{t("Add Another Sponsored Member", "إضافة فرد مكفول آخر")}</button> : null}</div>
      </div>

      <div className="family-member-list">
        {members.map((member, index) => (
          <section className={`family-member-block ${invalidMemberFields[index]?.length ? "has-validation-error" : ""}`} key={index}>
            <div className="family-member-heading">
              <span>{index + 1}</span>
              <div>
                <h4>{member.memberType === "SPOUSE" ? spouseLabel : member.memberType === "CHILD" ? t(`Child ${members.slice(0,index + 1).filter((item) => item.memberType === "CHILD").length}`, "طفل") : member.memberType === "SPONSORED" ? t("Sponsored Family Member", "فرد أسرة مكفول") : t(`Family Member ${index + 1}`, `فرد الأسرة ${index + 1}`)}</h4>
                <p>Enter the family member’s verified personal details.</p>
              </div>
              <button
                type="button"
                className="family-member-delete"
                onClick={() => deleteMember(index)}
                aria-label={t(`Delete dependent ${index + 1}`, `حذف المعال ${index + 1}`)}
              >
                <i className="fa-solid fa-trash"></i>
                {t("Delete", "حذف")}
              </button>
            </div>

            <div className="family-member-grid">
              <label>
                {t("Beneficiary ID", "رقم المستفيد")}
                <input value={beneficiaryId ? `BEN-${beneficiaryId}` : ""} readOnly />
              </label>
              <label>
                {t("Member Type", "نوع فرد الأسرة")}
                <select value={member.memberType} onChange={updateMember(index, "memberType")}><option value="SPOUSE">{spouseLabel}</option><option value="CHILD">{t("Child", "طفل")}</option><option value="SPONSORED">{t("Other Sponsored Member", "فرد مكفول آخر")}</option><option value="OTHER">{t("Other Family Member", "فرد أسرة آخر")}</option></select>
              </label>
              <label>
                {t("Relationship", "صلة القرابة")}
                <select value={member.relation} onChange={updateMember(index, "relation")} required disabled={member.memberType === "SPOUSE"} aria-invalid={invalidMemberFields[index]?.includes("relation") || undefined}>
                  <option value="">{t("Select relation", "اختر صلة القرابة")}</option>
                  {relations.map((relation) => (
                    <option key={relation.id} value={relation.id}>{(isArabic ? relation.title_ar : null) || relation.title_en || relation.title}</option>
                  ))}
                </select>
              </label>
              <label>
                {t("Family Member Name", "اسم فرد الأسرة")}
                <input value={member.name} onChange={updateMember(index, "name")} required aria-invalid={invalidMemberFields[index]?.includes("name") || undefined} />
              </label>
              <label>
                {t("Date of Birth", "تاريخ الميلاد")}
                <input type="date" value={member.dateOfBirth} onChange={updateMember(index, "dateOfBirth")} required aria-invalid={invalidMemberFields[index]?.includes("dateOfBirth") || undefined} />
              </label>
              <label>
                {t("Gender", "الجنس")}
                <select value={member.gender} onChange={updateMember(index, "gender")} disabled={member.memberType === "SPOUSE"}><option value="">{t("Select gender", "اختر الجنس")}</option><option value="Male">{t("Male", "ذكر")}</option><option value="Female">{t("Female", "أنثى")}</option></select>
              </label>
              <label>
                {t("Nationality", "الجنسية")}
                <select value={member.nationality} onChange={updateMember(index, "nationality")} required aria-invalid={invalidMemberFields[index]?.includes("nationality") || undefined}>
                  <option value="">{t("Select nationality", "اختر الجنسية")}</option>
                  {member.nationality && !nationalities.some((option) => String(option.id) === String(member.nationality)) ? <option value={member.nationality}>{member.nationality}</option> : null}
                  {nationalities.map((option) => <option key={option.id} value={String(option.id)}>{isArabic ? (option.title_ar || option.title_en) : option.title_en}</option>)}
                </select>
              </label>
              <label>
                {t("Age", "العمر")}
                <input type="number" min="0" max="120" value={member.age} onChange={updateMember(index, "age")} required aria-invalid={invalidMemberFields[index]?.includes("age") || undefined} />
              </label>
              <label>
                {t("Employment Status", "حالة العمل")}
                <select value={member.employmentStatus} onChange={updateEmploymentStatus(index)} required aria-invalid={invalidMemberFields[index]?.includes("employmentStatus") || undefined}>
                  <option value="">{t("Select employment status", "اختر حالة العمل")}</option>
                  {employmentStatusOptions.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </label>
              {requiresIncomeEvidence(member) ? <>
              <label>
                {t("Employer", "جهة العمل")}
                <input
                  value={member.employer}
                  onChange={updateMember(index, "employer")}
                  required
                  aria-invalid={invalidMemberFields[index]?.includes("employer") || undefined}
                />
              </label>
              <label>
                {t("Occupation", "المهنة")}
                <input
                  value={member.occupation}
                  onChange={updateMember(index, "occupation")}
                  required
                  aria-invalid={invalidMemberFields[index]?.includes("occupation") || undefined}
                />
              </label>
              <label>
                {t("Declared Monthly Income", "الدخل الشهري المصرح به")}
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={member.monthlyincome}
                  onChange={updateMember(index, "monthlyincome")}
                  required
                  aria-invalid={invalidMemberFields[index]?.includes("monthlyincome") || undefined}
                />
              </label>
              <label>
                {t("Declared Annual Income", "الدخل السنوي المصرح به")}
                <input type="number" min="0" step="0.01" value={member.annualincome} onChange={updateMember(index, "annualincome")} required aria-invalid={invalidMemberFields[index]?.includes("annualincome") || undefined} />
              </label>
              <label>
                {t("Other Income", "دخل آخر")}
                <input type="number" min="0" step="0.01" value={member.otherincome} onChange={updateMember(index, "otherincome")} required aria-invalid={invalidMemberFields[index]?.includes("otherincome") || undefined} />
              </label>
              <label>
                {t("Employment / Salary Certificate", "شهادة العمل / الراتب")}
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={updateCertificate(index)}
                  disabled={member.incomeVerificationStatus === "Verifying"}
                  aria-invalid={invalidMemberFields[index]?.includes("salaryCertificate") || undefined}
                />
                {member.salaryCertificate ? (
                  /^https?:\/\//i.test(member.salaryCertificate) ? (
                    <small><a href={member.salaryCertificate} target="_blank" rel="noreferrer">{t("View certificate", "عرض الشهادة")}</a></small>
                  ) : <small>{t("Certificate on file", "الشهادة محفوظة")}</small>
                ) : null}
                <small>{t("AI status", "حالة الذكاء الاصطناعي")}: {member.incomeVerificationStatus || "Pending"}</small>
              </label>
              <label>
                {t("Income Verification Status", "حالة التحقق من الدخل")}
                <input value={member.incomeVerificationStatus || "Pending"} readOnly aria-invalid={invalidMemberFields[index]?.includes("incomeVerificationStatus") || undefined} />
              </label>
              </> : null}
              <label>
                {t("Emirates ID Number", "رقم الهوية الإماراتية")}
                <input value={member.eidnumber} onChange={updateMember(index, "eidnumber")} maxLength="30" required aria-invalid={invalidMemberFields[index]?.includes("eidnumber") || undefined} />
              </label>
              <label>
                {t("Emirates ID Expiry Date", "تاريخ انتهاء الهوية الإماراتية")}
                <input type="date" value={member.expirydate} onChange={updateMember(index, "expirydate")} required aria-invalid={invalidMemberFields[index]?.includes("expirydate") || undefined} />
              </label>
              <label>{t("Passport Number", "رقم جواز السفر")}<input value={member.passportNumber} onChange={updateMember(index, "passportNumber")} /></label>
              <label>{t("Passport Expiry", "انتهاء جواز السفر")}<input type="date" value={member.passportExpiry} onChange={updateMember(index, "passportExpiry")} /></label>
              <label>{t("Residency Status", "حالة الإقامة")}<select value={member.residencyStatus} onChange={updateMember(index, "residencyStatus")}><option value="">{t("Select residency status", "اختر حالة الإقامة")}</option>{member.residencyStatus && !residencyStatuses.some((option) => String(option.id) === String(member.residencyStatus)) ? <option value={member.residencyStatus}>{member.residencyStatus}</option> : null}{residencyStatuses.map((option) => <option key={option.id} value={String(option.id)}>{isArabic ? (option.title_ar || option.title_en) : option.title_en}</option>)}</select></label>
              <label>{t("Residency Expiry", "انتهاء الإقامة")}<input type="date" value={member.residencyExpiry} onChange={updateMember(index, "residencyExpiry")} /></label>
              {member.memberType === "SPOUSE" ? <label className="family-field-wide">{t("Marriage Details", "تفاصيل الزواج")}<textarea value={member.marriageDetails} onChange={updateMember(index, "marriageDetails")} /></label> : null}
              {member.memberType === "SPONSORED" ? <><label>{t("Sponsorship Status", "حالة الكفالة")}<input value={member.sponsorshipStatus} onChange={updateMember(index, "sponsorshipStatus")} /></label><label className="family-field-wide">{t("Sponsorship Details", "تفاصيل الكفالة")}<textarea value={member.sponsorshipDetails} onChange={updateMember(index, "sponsorshipDetails")} /></label></> : null}
              <label>{t("Verification Status", "حالة التحقق")}<input value={member.verificationStatus || "Review Required"} readOnly /></label>
            </div>
            <div className="family-document-upload">
              <strong>{t("Supporting documents", "المستندات الداعمة")}</strong>
              <select value={documentDrafts[index]?.category || "EMIRATES_ID"} onChange={updateDocumentDraft(index, "category")}>
                <option value="EMIRATES_ID">{t("Emirates ID", "الهوية الإماراتية")}</option>
                <option value="PASSPORT">{t("Passport", "جواز السفر")}</option>
                <option value="RESIDENCY">{t("Residency", "الإقامة")}</option>
                {member.memberType === "SPOUSE" ? <option value="MARRIAGE_CERTIFICATE">{t("Marriage Certificate", "شهادة الزواج")}</option> : null}
                <option value="EMPLOYMENT_EVIDENCE">{t("Employment Evidence", "إثبات العمل")}</option>
                <option value="OTHER">{t("Other Supporting Evidence", "إثبات داعم آخر")}</option>
              </select>
              <input type="date" value={documentDrafts[index]?.expiryDate || ""} onChange={updateDocumentDraft(index, "expiryDate")} aria-label={t("Document expiry date", "تاريخ انتهاء المستند")} />
              <input type="file" accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png" onChange={uploadMemberDocuments(index)} disabled={documentDrafts[index]?.busy} />
              <small>{documentDrafts[index]?.pendingFileNames?.length
                ? t(`Selected: ${documentDrafts[index].pendingFileNames.join(", ")}. Save Family Details to upload.`, `تم الاختيار: ${documentDrafts[index].pendingFileNames.join(", ")}. احفظ بيانات الأسرة للرفع.`)
                : member.id
                  ? t("Select one file. Existing files will remain saved.", "اختر ملفاً واحداً. ستبقى الملفات السابقة محفوظة.")
                  : t("Select a file now. It will upload after this member is saved.", "اختر ملفاً الآن. سيتم رفعه بعد حفظ هذا الفرد.")}</small>
            </div>
            {familyDocuments.filter((document) => Number(document.familyMemberId) === Number(member.id)).length ? <div className="family-document-history"><strong>{t("Saved documents", "المستندات المحفوظة")}</strong>{familyDocuments.filter((document) => Number(document.familyMemberId) === Number(member.id)).map((documentRecord) => <button className="family-document-link" type="button" key={documentRecord.id} onClick={() => openDocumentPreview(documentRecord, member.name)}><i className="fa-regular fa-eye" aria-hidden="true"></i>{documentRecord.title} · {documentRecord.verificationStatus}</button>)}</div> : null}
          </section>
        ))}
      </div>

      <button className="submit-btn family-details-save" type="submit" disabled={isSaving || members.some((member) => member.incomeVerificationStatus === "Verifying")}>
        {isSaving ? t("Saving...", "جارٍ الحفظ...") : t("Save Family Details", "حفظ بيانات الأسرة")}
      </button>
    </form>
    {previewDocument && typeof document !== "undefined" ? createPortal((
      <div className="mag-pdf-modal family-document-preview-modal" role="dialog" aria-modal="true" aria-label={t("Document preview", "معاينة المستند")} dir={isArabic ? "rtl" : undefined}>
        <button className="mag-pdf-backdrop" type="button" aria-label={t("Close document preview", "إغلاق معاينة المستند")} onClick={() => setPreviewDocument(null)}></button>
        <div className="mag-pdf-dialog">
          <div className="mag-pdf-head">
            <div><h3>{previewDocument.title}</h3><p>{previewDocument.memberName} · {previewDocument.verificationStatus}</p></div>
            <div className="mag-pdf-actions">
              {previewDocument.objectUrl ? <a href={previewDocument.objectUrl} download={previewDocument.title}>{t("Download", "تنزيل")}</a> : null}
              <button type="button" aria-label={t("Close document preview", "إغلاق معاينة المستند")} onClick={() => setPreviewDocument(null)}><i className="fa-solid fa-xmark"></i></button>
            </div>
          </div>
          {previewDocument.loading ? <div className="family-document-preview-status"><i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>{t("Loading document...", "جارٍ تحميل المستند...")}</div> : previewDocument.error ? <div className="family-document-preview-status is-error" role="alert">{previewDocument.error}</div> : <iframe src={previewDocument.objectUrl} title={`${previewDocument.title} ${t("document preview", "معاينة المستند")}`}></iframe>}
        </div>
      </div>
    ), document.body) : null}
    {aiDocumentModal && typeof document !== "undefined" ? createPortal((
      <div className={`aid-ai-modal is-open ${aiDocumentModal.state === "loading" ? "is-loading" : `is-${aiDocumentModal.state}`}`} role="dialog" aria-modal="true" aria-labelledby="familyCertificateModalTitle" onMouseDown={() => aiDocumentModal.state !== "loading" && setAiDocumentModal(null)}>
        <div className="aid-ai-modal-card" onMouseDown={(event) => event.stopPropagation()}>
          {aiDocumentModal.state !== "loading" ? <button className="aid-ai-close" type="button" aria-label={t("Close popup", "إغلاق النافذة")} onClick={() => setAiDocumentModal(null)}><i className="fa-solid fa-xmark"></i></button> : null}
          <div className="aid-ai-robot">
            <img src="/images/img/rahma-ai.png" alt="" />
            {aiDocumentModal.state !== "loading" ? <span className={`aid-ai-status-mark is-${aiDocumentModal.state}`}><i className={`fa-solid ${aiDocumentModal.state === "accepted" ? "fa-check" : aiDocumentModal.state === "rejected" ? "fa-xmark" : "fa-clock"}`}></i></span> : null}
          </div>
          {aiDocumentModal.state === "loading" ? <><div className="aid-ai-loader" aria-hidden="true"></div><h3 id="familyCertificateModalTitle">{documentLoadingCopy.title}</h3><p>{documentLoadingCopy.message}</p></> : <>
            <h3 id="familyCertificateModalTitle">{modalDetails.title}</h3>
            <p>{modalDetails.message}</p>
            <div className="aid-ai-notice">{modalDetails.notice}</div>
            <div className="aid-ai-actions"><button className="aid-ai-primary" type="button" onClick={() => setAiDocumentModal(null)}>{modalDetails.button}</button></div>
          </>}
        </div>
      </div>
    ), document.body) : null}
    </>
  );
}
